import type { ContractTypeAutoConfirmationKind, ContractTypeConfirmation } from '@clm/shared-types';

import {
  AUTO_APPROVED_BY_POLICY_TAG_TITLE,
  AUTO_CONFIRMATION_TAG_COPY,
  AUTO_CONFIRMED_ANY_TAG_TITLE,
  AUTO_CONFIRMED_TAG_TITLE,
  PENDING_TAG_TITLE,
  PROVISIONAL_TAG_TITLE,
} from '../surfaces/type-proposal-copy';

/**
 * A contract's type, as every surface renders it — CLM-110 (R-110-23), CLM-118
 * (R-118-12), CLM-121 (R-121-14).
 *
 * A type is printed bare only when an administrator has confirmed it. Anywhere
 * else a bare type would look settled, which is D-2.1-4's failure applied to the
 * type, so the type is never printed on its own where a confirmation travels
 * with it. Four renderings, each tag with a `title` that says who settles it:
 *
 *  - `confirmed` — the type alone: the absence of a tag is the message;
 *  - `provisional` — the type and a neutral `provisional` tag;
 *  - `auto_confirmed` — the type and what settled it with no person, by
 *    `autoKind`: "file name and AI agree" (D-110-3, 1a), "approved by policy"
 *    (D-110-4, CLM-121), or — on a surface that holds only the confirmation —
 *    the kind-neutral "confirmed automatically", whose title names both. A type
 *    nobody checked never reads as an administrator's (B8, default 3);
 *  - `pending` — **"Type pending"** and no type word at all: the stored `OTHER`
 *    is a placeholder, and printing it would count it as OTHER (B19).
 *
 * Neutral, not a warning tone: an unsettled type is the normal state of every
 * contract between intake and the AI's reading or an Admin's decision, not a
 * fault. Display only — nothing reads the tag for a rule (the base-MSA refusal
 * reads the confirmation itself, through `baseContractIneligibility`).
 */
export function ContractTypeTag({
  type,
  confirmation,
  autoKind,
}: {
  readonly type: string;
  readonly confirmation: ContractTypeConfirmation;
  /** CLM-121 — the act's kind, where the surface holds it; absent prints "confirmed automatically". */
  readonly autoKind?: ContractTypeAutoConfirmationKind | null;
}) {
  if (confirmation === 'pending') {
    return (
      <span className="ctype" data-confirmation={confirmation}>
        <span className="tag" title={PENDING_TAG_TITLE} data-testid="contract-type-pending">
          Type pending
        </span>
      </span>
    );
  }
  const kind = autoKind ?? 'unknown';
  const autoTitle =
    kind === 'file_name_and_ai_agree'
      ? AUTO_CONFIRMED_TAG_TITLE
      : kind === 'auto_approval_policy'
        ? AUTO_APPROVED_BY_POLICY_TAG_TITLE
        : AUTO_CONFIRMED_ANY_TAG_TITLE;
  return (
    <span className="ctype" data-confirmation={confirmation}>
      {type}
      {confirmation === 'provisional' ? (
        <>
          {' '}
          <span className="tag" title={PROVISIONAL_TAG_TITLE} data-testid="contract-type-provisional">
            provisional
          </span>
        </>
      ) : confirmation === 'auto_confirmed' ? (
        <>
          {' · '}
          <span className="tag" title={autoTitle} data-testid="contract-type-auto-confirmed" data-auto-kind={kind}>
            {AUTO_CONFIRMATION_TAG_COPY[kind]}
          </span>
        </>
      ) : null}
    </span>
  );
}
